import React, { useMemo } from "react";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { Button } from "../../../../ui/components/Button";
import {
  formatBulkUploadCellValue,
  formatBulkUploadFieldLabel,
} from "../../../../shared/presentation/utils/bulkUploadFieldLabels";
import { resolveBulkUploadReviewColumns } from "../../../../shared/presentation/utils/excelTemplateColumns";

export interface BulkUploadReviewRowsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: "customer" | "vendor" | "gl";
  rows: Array<{ excel_row: number; payload: Record<string, unknown> }>;
}

export const BulkUploadReviewRowsModal: React.FC<
  BulkUploadReviewRowsModalProps
> = ({ open, onOpenChange, entityType, rows }) => {
  const columns = useMemo(
    () => resolveBulkUploadReviewColumns(entityType, rows),
    [entityType, rows],
  );

  const title =
    entityType === "customer"
      ? "Bulk Customer Import — rows"
      : entityType === "vendor"
        ? "Bulk Vendor Import — rows"
        : "Bulk GL Import — rows";

  return (
    <DialogLayout
      open={open}
      onOpenChange={onOpenChange}
      className="flex max-h-[90vh] w-full min-w-[min(960px,100vw-2rem)] max-w-[1100px] flex-col overflow-hidden"
    >
      <div className="flex max-h-[90vh] w-full flex-col overflow-hidden">
        <div className="flex w-full flex-none flex-wrap items-center justify-between gap-3 border-b border-solid border-neutral-border p-5">
          <span className="text-heading-3 font-heading-3 text-default-font pr-4">
            {title}
          </span>
          <Button
            variant="neutral-secondary"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-4">
          {rows.length === 0 ? (
            <span className="text-body font-body text-subtext-color">
              No staged rows.
            </span>
          ) : (
            <div className="w-full overflow-x-auto rounded-md border border-solid border-neutral-border">
              <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-solid border-neutral-border bg-neutral-50">
                    {columns.map((col) => (
                      <th
                        key={col}
                        className="whitespace-nowrap px-3 py-2 font-semibold text-default-font"
                      >
                        {formatBulkUploadFieldLabel(col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr
                      key={`${r.excel_row}-${idx}`}
                      className="border-b border-solid border-neutral-border last:border-0"
                    >
                      {columns.map((col) => (
                        <td
                          key={col}
                          className="max-w-[220px] truncate px-3 py-2 text-default-font"
                          title={formatBulkUploadCellValue(
                            col,
                            r.payload?.[col],
                          )}
                        >
                          {formatBulkUploadCellValue(col, r.payload?.[col])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DialogLayout>
  );
};